import { useMemo } from 'react'

import type { GameSummary } from '../api/client'
import { generatedPalette } from '../game/symbols'
import { useGameStore } from '../store/gameStore'

/**
 * Game preview for the lobby.
 *
 * This is neither a screenshot nor art: it is the same grid, in the same
 * colours the game is drawn with on the board. That is why a new game gets
 * a recognisable preview automatically the moment it shows up in the
 * releases — without a single image file.
 *
 * The colours come from the game's theme when one is loaded, and from the
 * generated placeholder before that. So a card starts neutral and turns
 * into the skin's palette as soon as the theme arrives — one source of
 * truth for the board and the shelf, which is what keeps a themed game
 * recognisable in the lobby.
 */

/** How many cells we show in a preview — more simply does not read. */
const MAX_COLS = 6
const MAX_ROWS = 5

/**
 * Deterministic generator: a game preview must not flicker on every
 * render of the list. The seed comes from the gameId, so the picture is
 * stable across sessions.
 */
function seeded(seed: number): () => number {
  let s = seed || 1
  return () => {
    s ^= s << 13
    s ^= s >>> 17
    s ^= s << 5
    return ((s >>> 0) % 1000) / 1000
  }
}

function seedOf(text: string): number {
  let h = 2166136261
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

interface Cell {
  code: string
  fill: string
  scatter: boolean
}

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`
}

export function GamePreview({ game }: { game: GameSummary }): React.JSX.Element {
  const theme = useGameStore((s) => s.themes[game.gameId])
  const { cells, cols, rows } = useMemo(() => {
    const palette = theme?.symbols ?? generatedPalette(game.symbols)
    const rnd = seeded(seedOf(game.gameId))

    // Weights by role: scatter and wild are rare accents, high and low
    // symbols are equally likely. The game's real weights are neither
    // needed here nor available to the client; with them the preview
    // would come out almost single-coloured, because low symbols are
    // always the majority on screen.
    const weight = (kind: string): number =>
      kind === 'scatter' || kind === 'wild' ? 1 : 4
    const pool = game.symbols.flatMap((s) => Array<typeof s>(weight(s.kind)).fill(s))

    const cols = Math.min(game.layout.reels, MAX_COLS)
    const rows = Math.min(game.layout.rows, MAX_ROWS)
    const cells: Cell[] = []
    for (let i = 0; i < cols * rows; i++) {
      const sym = pool[Math.floor(rnd() * pool.length)] ?? game.symbols[0]
      const style = palette[sym.code]
      cells.push({
        code: sym.code,
        fill: hex(style?.fill ?? 0x2a2f3a),
        scatter: sym.kind === 'scatter',
      })
    }
    return { cells, cols, rows }
  }, [game, theme])

  return (
    <div
      className="preview"
      style={{
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        // The ratio comes from the grid itself, so cells stay square at any
        // board size. The card height stays constant regardless: that is
        // held by .card__art.
        aspectRatio: `${cols} / ${rows}`,
        // Stretch along whichever side hits the box first. Without an
        // explicit side the grid has zero size inside a flex container.
        ...(cols / rows >= 6 / 5
          ? { width: '100%', height: 'auto' }
          : { width: 'auto', height: '100%' }),
      }}
      aria-hidden="true"
    >
      {cells.map((cell, i) => (
        <span
          key={i}
          className={`preview__cell ${cell.scatter ? 'preview__cell--scatter' : ''}`}
          style={{ background: cell.fill }}
        />
      ))}
    </div>
  )
}
